import api from "@client/content/modules/Checkout/api";
import { withOnSubmit } from "@client/content/modules/Checkout/hooks";
import { type Checkout } from "@client/content/modules/Checkout/types";
import { type FormHandles } from "@client/controls/Form/component";
import { unwrapResponsePromise } from "@client/lib/api";
import { getGoogleFontLinkFromNames, isGoogleFontLabel } from "@client/lib/fonts";
import StripeCardInputFormField from "@common/components/Form/Field/StripeCard/component";
import { type StripeCustomerInfo } from "@common/components/Form/Field/StripeCard/lib";
import { FormStyleContext } from "@common/contexts/FormStyle";
import { FormControlled, type FormControlledRenderParams } from "@common/controls/Form/component";
import { EMPTY_OBJECT } from "@common/lib/misc";
import { identity } from "lodash";
import React from "react";
import { type PaymentMethodBaseProps } from "../../component";
import StripePaymentInputFormField from "../PaymentElement/StripePaymentInputFormField";

type Props = PaymentMethodBaseProps & {
  amount: number;
  currencyCode: string;
  parameters: Checkout.IntegrationParameter[];
  hasSubscriptionItems: boolean;
};

const CheckoutPaymentStripeCreditCard = (props: Props) => {
  const publishKey = React.useMemo(
    () => props.parameters.find((x) => x.name === "StripePublishableKey")!.value,
    [props.parameters]
  );

  const [data, setData] = React.useState<Checkout.View.StripeCreditCardPayment>({});

  const ref = withOnSubmit<FormHandles>(({ paymentIntentId, ...data }: Checkout.View.StripeCreditCardPayment) => ({
    paymentInfo: {
      ...data.paymentInfo,
      paymentIntentId: data.paymentInfo?.paymentIntentId ?? paymentIntentId,
      type: "Stripe"
    }
  }));

  const customer = React.useMemo(
    (): StripeCustomerInfo => ({
      email: props.billingShipingInfo.billingEmail ?? "",
      firstName: props.billingShipingInfo.billingFirstName ?? "",
      lastName: props.billingShipingInfo.billingLastName ?? ""
    }),
    [
      props.billingShipingInfo.billingEmail,
      props.billingShipingInfo.billingFirstName,
      props.billingShipingInfo.billingLastName
    ]
  );

  const onChange = React.useCallback(
    (name: keyof Checkout.View.StripeCreditCardPayment, value: Checkout.View.StripePaymentInfo | undefined) =>
      setData({
        ...data,
        [name]: value
      }),
    [setData]
  );

  const { fieldStyle } = React.useContext(FormStyleContext);

  const onCreateIntent = React.useCallback(async () => {
    const res = await unwrapResponsePromise(
      api.payment(props.apiInfo, {
        paymentMethodId: props.paymentMethodId!,
        billingShipingInfo: props.billingShipingInfo
      })
    );

    return {
      isSetup: !props.amount,
      secret: res.clientSecret
    };
  }, []);

  const fontCssSrc = React.useMemo(
    () =>
      props.fieldStyle.fontFamily && isGoogleFontLabel(props.fieldStyle.fontFamily)
        ? getGoogleFontLinkFromNames([props.fieldStyle.fontFamily])
        : undefined,
    [props.fieldStyle.fontFamily]
  );

  return (
    <FormControlled ref={ref as any} onChange={onChange} submit={identity} defaultValues={EMPTY_OBJECT}>
      {({ values }: FormControlledRenderParams<Checkout.View.StripeCreditCardPayment>) => (
        <>
          {props.amount === 0 ? (
            <StripeCardInputFormField
              amount={props.amount}
              createIntent={onCreateIntent}
              currencyCode={props.currencyCode}
              customer={customer}
              fontCssSrc={fontCssSrc}
              inputStyle={fieldStyle}
              isPaddingOnContainer
              isRequired
              name="paymentIntentId"
              publishableKey={publishKey}
              qa="Payment token"
              value={values.paymentIntentId}
            />
          ) : (
            <StripePaymentInputFormField
              amount={props.amount}
              createIntent={onCreateIntent}
              currencyCode={props.currencyCode}
              customer={customer}
              fontCssSrc={fontCssSrc}
              hasSubscriptionItems={props.hasSubscriptionItems}
              isPaddingOnContainer
              isRequired
              name="paymentInfo"
              publishableKey={publishKey}
              qa="Payment info"
              value={values.paymentInfo}
            />
          )}
        </>
      )}
    </FormControlled>
  );
};

export default CheckoutPaymentStripeCreditCard;
